// BuildOverview/BuildOverviewEngine.js
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — REST fetch for initial load / reload,
// live SignalR push (OnBuildStatusUpdated) with the full job list. Singleton.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import buildOverviewCache from './BuildOverviewCache';
import buildOverviewFetcher from './BuildOverviewFetcher';
import { FLASH_MS } from './buildOverviewConst';

const _name = 'BuildOverviewEngine';

class BuildOverviewEngine extends WssEventHandlerBase {
    _name = _name;
    _failed = false;

    // rk → epoch ms of last state change, drives the row flash
    _changedAt = new Map();

    // no this.clear() here, it would empty the cache (FE Engine Pattern V2 pitfall)
    bootup() {
        super.bootup();
        this._unsub = this._wsNotifier.subscribe(events.OnBuildStatusUpdated, this.onMessage);
    }

    clear() {
        super.clear();
        buildOverviewCache.clear();
        this._changedAt.clear();
        this._failed = false;
        if (this._unsub) this._unsub();
    }

    /**
     * BE push — 1 or more BuildStatus, merged per rk.
     * @param {object[]} data
     */
    onMessage = (data) => {
        try {
            if (!Array.isArray(data)) return;

            this._markChanged(data);
            buildOverviewCache.addOrUpdate(data);
            this._notify();
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * Initial fetch and manual reload — full list from the gateway cache.
     * @returns {Promise<void>}
     */
    fetch = async () => {
        try {
            const items = await buildOverviewFetcher.fetch();
            this._failed = items === null;
            if (items) {
                this._markChanged(items);
                buildOverviewCache.setAll(items);
            }

            this._notify();
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    getAll = () => buildOverviewCache.getAll();

    /**
     * Start a job, building state arrives later via OnBuildStatusUpdated (no optimistic update).
     * @param {string} name - job name
     * @returns {Promise<boolean>} true when the gateway accepted the request
     */
    triggerBuild = async (name) => {
        try {
            return await buildOverviewFetcher.postJobs([name]);
        } catch (error) {
            this.logError('triggerBuild', error.message);
            return false;
        }
    };

    /**
     * @param {string} rk
     * @returns {boolean} true while the row state changed less than FLASH_MS ago
     */
    isFlashing = (rk) => {
        const at = this._changedAt.get(rk);
        if (!at) return false;

        return Date.now() - at < FLASH_MS;
    };

    /**
     * Compares incoming items with the cache, call BEFORE setAll. First load never flashes.
     * @param {object[]} items
     */
    _markChanged(items) {
        const now = Date.now();
        items.forEach((i) => this._checkChanged(i, now));
    }

    _checkChanged(item, now) {
        const prev = buildOverviewCache.get(item.rk);
        if (!prev) return;

        const changed = prev.lastResult !== item.lastResult || prev.lastNumber !== item.lastNumber;
        if (!changed) return;

        this._changedAt.set(item.rk, now);
    }

    hasError = () => this._failed;

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {});
    };
}

const buildOverviewEngine = new BuildOverviewEngine();
export default buildOverviewEngine;
